import React, { useState, useEffect, useMemo } from 'react';
import statesData from '../../data/nigeriaStatesLgas.json';
import { listZones } from '../../services/zoneService';

// Shared State -> Local Government picker used everywhere a "zone" is
// selected: the therapist profile (multi-select), the venue profile
// (single-select), and the booking form (single-select) all go through this
// so the underlying Zone records — and matching logic — stay consistent
// platform-wide. The client's own address field covers precise location;
// the zone only needs to resolve to a local government area.
const ZonePicker = ({ mode = 'single', value, onChange }) => {
  const [zones, setZones] = useState([]);
  const [selectedState, setSelectedState] = useState('');

  useEffect(() => { listZones().then(setZones); }, []);

  const lgasForState = useMemo(() => {
    const state = statesData.find(s => s.name === selectedState);
    return state ? state.lgas : [];
  }, [selectedState]);

  const zoneIdFor = (state, lga) => zones.find(z => z.state === state && z.lga === lga)?._id;

  const selectedIds = mode === 'multi' ? (value || []) : (value ? [value] : []);

  const toggleZone = (zoneId) => {
    if (!zoneId) return;
    if (mode === 'multi') {
      const next = selectedIds.includes(zoneId)
        ? selectedIds.filter(id => id !== zoneId)
        : [...selectedIds, zoneId];
      onChange(next);
    } else {
      onChange(zoneId === value ? '' : zoneId);
    }
  };

  // Show human-readable labels for already-selected zones even before a
  // state is picked in the dropdown above.
  const selectedZoneObjects = zones.filter(z => selectedIds.includes(z._id));

  return (
    <div className="space-y-3">
      {selectedZoneObjects.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedZoneObjects.map(z => (
            <span key={z._id} className="flex items-center gap-1.5 bg-primary-dark text-secondary px-3 py-1 rounded-full text-xs">
              {z.lga}, {z.state}
              <button type="button" onClick={() => toggleZone(z._id)} className="hover:text-accent-light">×</button>
            </span>
          ))}
        </div>
      )}

      <select
        value={selectedState}
        onChange={(e) => setSelectedState(e.target.value)}
        className="w-full p-2.5 border rounded-lg text-sm"
      >
        <option value="">Select state</option>
        {statesData.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
      </select>

      {selectedState && (
        <div className="flex flex-wrap gap-2">
          {lgasForState.map(lga => {
            const zoneId = zoneIdFor(selectedState, lga);
            const selected = zoneId && selectedIds.includes(zoneId);
            return (
              <button
                type="button"
                key={lga}
                onClick={() => toggleZone(zoneId)}
                disabled={!zoneId}
                className={`px-3 py-1 rounded-full border text-sm disabled:opacity-40 ${
                  selected ? 'bg-primary-dark text-secondary border-primary-dark' : 'bg-white text-dim border-gray-300'
                }`}
              >
                {lga}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ZonePicker;
